import { memo, useMemo, useState, useCallback } from "react";
import { Button, Divider, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ButtonLoading from "../../../widgets/ButtonLoading/ButtonLoading";
import CancelButton from "../../../widgets/CancelButton/CancelButton";
import { getTranslationKeyError } from "../utils/translationValidation";

function TranslationForm({
  baseKeys,
  values,
  onChange,           // (key, val)
  onRemoveExtraKey,   // (key)
  onSubmit,           // (allValues)
  referenceTranslations,
  busy,
}) {
  // local draft input for adding key/value (fixes “value disappears” bug)
  const [draftKey, setDraftKey] = useState("");
  const [draftVal, setDraftVal] = useState("");

  const fieldKeys = useMemo(() => {
    const extra = Object.keys(values).filter((k) => !baseKeys.includes(k));
    return [...baseKeys, ...extra];
  }, [baseKeys, values]);

  const addDisabled = useMemo(() => {
    if (getTranslationKeyError(draftKey)) return true;
    if ((draftVal ?? "").trim() === "") return true;
    if (Object.prototype.hasOwnProperty.call(values, draftKey)) return true; // unique
    return false;
  }, [draftKey, draftVal, values]);

  const handleAdd = useCallback(() => {
    if (addDisabled) return;
    onChange(draftKey, draftVal);  // preserve the entered value
    setDraftKey("");
    setDraftVal("");
  }, [addDisabled, draftKey, draftVal, onChange]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onSubmit(values);
    },
    [onSubmit, values]
  );

  return (
    <form className="flex flex-1 space-y-8 flex-col" onSubmit={handleSubmit}>
      <Divider sx={{ mb: 2 }} />

      {fieldKeys.map((k) => {
        const isBase = baseKeys.includes(k);
        return (
          <div key={k} className="flex items-start space-x-2">
            <TextField
              fullWidth
              label={`Champ ${k}`}
              size="small"
              name={k}
              value={values[k] ?? ""}
              onChange={(e) => onChange(k, e.target.value)}
              helperText={
                <span className="italic">
                  Texte en anglais :{" "}
                  <span className="font-semibold">
                    {referenceTranslations?.[k] ?? "(aucun exemple)"}
                  </span>
                </span>
              }
            />
            {!isBase && (
              <Tooltip title="Supprimer ce champ ajouté">
                <IconButton size="small" onClick={() => onRemoveExtraKey(k)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      })}

      {/* Add key/value controls with validation */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="Ajouter un nouveau champ (clé)"
          size="small"
          value={draftKey}
          onChange={(e) => setDraftKey(e.target.value)}
          error={!!getTranslationKeyError(draftKey)}
          helperText={getTranslationKeyError(draftKey) || "ex: subtitle"}
        />
        <TextField
          label="Valeur"
          size="small"
          value={draftVal}
          onChange={(e) => setDraftVal(e.target.value)}
          error={(draftVal ?? "").trim() === ""}
          helperText={(draftVal ?? "").trim() === "" ? "Valeur requise" : "ex: Bienvenue !"}
        />
        <Button variant="outlined" onClick={handleAdd} disabled={addDisabled}>
          Ajouter le champ
        </Button>
      </Stack>

      <div className="flex mt-auto justify-end items-center gap-4">
        <CancelButton disabled={busy} label="Annuler" />
        <ButtonLoading loading={busy} variant="contained" type="submit">
          Sauvegarder
        </ButtonLoading>
      </div>
    </form>
  );
}

export default memo(TranslationForm);
